import L from "leaflet";

import type { ApiClient } from "./api";
import { friendlyMessage } from "./api";
import { saveViewport } from "./geo";
import type { Bbox, OsmToilet, Toilet, UserToilet, Viewport, Visit } from "./types";

const MOVE_DEBOUNCE_MS = 400;

export interface MapBundle {
  map: L.Map;
  osmLayer: L.LayerGroup;
  userLayer: L.LayerGroup;
  visitedOverlay: L.LayerGroup;
  refresh: () => void;
  setVisited: (visits: Visit[]) => void;
  setUserLocation: (coords: { lat: number; lon: number } | null, accuracyM?: number) => void;
  destroy: () => void;
  onSelect: (cb: (t: Toilet) => void) => void;
  onEmpty: (cb: (empty: boolean) => void) => void;
}

interface CreateOpts {
  container: HTMLElement;
  api: ApiClient;
  initial: Viewport;
  onError?: (msg: string) => void;
  ownDeviceId: string;
}

function bboxFromMap(map: L.Map): Bbox {
  const b = map.getBounds();
  return [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()];
}

function bboxAreaDeg2(b: Bbox): number {
  return (b[2] - b[0]) * (b[3] - b[1]);
}

function osmIcon(): L.DivIcon {
  return L.divIcon({
    className: "pin pin-osm",
    html: '<span class="pin-bg"></span><span class="pin-glyph">🚽</span>',
    iconSize: [38, 46],
    iconAnchor: [19, 44],
  });
}

function userPublicIcon(): L.DivIcon {
  return L.divIcon({
    className: "pin pin-user",
    html: '<span class="pin-bg"></span><span class="pin-glyph">🚻</span>',
    iconSize: [38, 46],
    iconAnchor: [19, 44],
  });
}

function userPersonalIcon(): L.DivIcon {
  return L.divIcon({
    className: "pin pin-personal",
    html:
      '<span class="pin-bg"></span><span class="pin-glyph">⭐</span>' +
      '<span class="pin-pill">personal</span>',
    iconSize: [44, 50],
    iconAnchor: [22, 48],
  });
}

function visitedBadgeIcon(visit_type: 1 | 2): L.DivIcon {
  return L.divIcon({
    className: "pin pin-visited",
    html: `<span class="pin-glyph">${visit_type === 2 ? "💩" : "💧"}</span>`,
    iconSize: [22, 22],
    iconAnchor: [-6, 32], // offset right of the underlying pin so both are visible
  });
}

function userPositionIcon(): L.DivIcon {
  return L.divIcon({
    className: "user-position",
    html: '<span class="user-position-dot"></span><span class="user-position-pulse"></span>',
    iconSize: [22, 22],
    iconAnchor: [11, 11],
  });
}

export function createMap(opts: CreateOpts): MapBundle {
  const { container, api, initial, onError, ownDeviceId } = opts;

  const map = L.map(container, {
    zoomControl: true,
    preferCanvas: false,
  }).setView([initial.lat, initial.lon], initial.zoom);

  L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
    maxZoom: 19,
    attribution: "© OpenStreetMap contributors",
  }).addTo(map);

  const osmLayer = L.layerGroup().addTo(map);
  const userLayer = L.layerGroup().addTo(map);
  const visitedOverlay = L.layerGroup().addTo(map);
  const userPositionLayer = L.layerGroup().addTo(map);
  let accuracyCircle: L.Circle | null = null;

  let inFlight: AbortController | null = null;
  let moveTimer: number | undefined;
  let lastEmpty = false;
  const selectListeners: Array<(t: Toilet) => void> = [];
  const emptyListeners: Array<(empty: boolean) => void> = [];

  const dispatchSelect = (t: Toilet): void => {
    for (const cb of selectListeners) cb(t);
  };
  const dispatchEmpty = (empty: boolean): void => {
    if (empty === lastEmpty) return;
    lastEmpty = empty;
    for (const cb of emptyListeners) cb(empty);
  };

  const renderOsm = (toilets: OsmToilet[]): void => {
    osmLayer.clearLayers();
    for (const t of toilets) {
      const m = L.marker([t.lat, t.lon], { icon: osmIcon() });
      m.on("click", () => dispatchSelect(t));
      osmLayer.addLayer(m);
    }
  };

  const renderUser = (toilets: UserToilet[]): void => {
    userLayer.clearLayers();
    for (const t of toilets) {
      const isOwnPersonal = !t.is_public;
      const m = L.marker([t.lat, t.lon], {
        icon: isOwnPersonal ? userPersonalIcon() : userPublicIcon(),
      });
      m.on("click", () => dispatchSelect(t));
      userLayer.addLayer(m);
    }
  };

  const refresh = async (): Promise<void> => {
    const bbox = bboxFromMap(map);
    if (bboxAreaDeg2(bbox) > 0.25) {
      // Map is zoomed too far out for a server query; clear and bail.
      osmLayer.clearLayers();
      userLayer.clearLayers();
      dispatchEmpty(false);
      return;
    }
    if (inFlight) inFlight.abort();
    inFlight = new AbortController();
    const signal = inFlight.signal;
    try {
      const [osm, user] = await Promise.all([
        api.getOsmToilets(bbox, signal).catch((e) => {
          if (signal.aborted) return [] as OsmToilet[];
          throw e;
        }),
        api.getUserToilets(bbox, signal).catch((e) => {
          if (signal.aborted) return [] as UserToilet[];
          throw e;
        }),
      ]);
      if (signal.aborted) return;
      renderOsm(osm);
      renderUser(user);
      dispatchEmpty(osm.length === 0 && user.length === 0);
    } catch (err) {
      if ((err as { name?: string }).name === "AbortError") return;
      if (onError) onError(friendlyMessage(err));
    }
  };

  const setUserLocation = (
    coords: { lat: number; lon: number } | null,
    accuracyM?: number,
  ): void => {
    userPositionLayer.clearLayers();
    if (accuracyCircle) {
      map.removeLayer(accuracyCircle);
      accuracyCircle = null;
    }
    if (!coords) return;
    if (accuracyM && accuracyM > 0 && accuracyM < 10_000) {
      accuracyCircle = L.circle([coords.lat, coords.lon], {
        radius: accuracyM,
        color: "#1f6feb",
        fillColor: "#1f6feb",
        fillOpacity: 0.08,
        weight: 1,
        interactive: false,
      }).addTo(map);
    }
    const dot = L.marker([coords.lat, coords.lon], {
      icon: userPositionIcon(),
      interactive: false,
      keyboard: false,
      zIndexOffset: 500,
    });
    userPositionLayer.addLayer(dot);
  };

  const setVisited = (visits: Visit[]): void => {
    visitedOverlay.clearLayers();
    // For each visit, drop a small badge on the toilet. We rely on hydrated_metadata.lat/lon
    // when present (user / snapshot). osm-live entries without metadata are skipped here.
    for (const v of visits) {
      const md = v.hydrated_metadata as { lat?: number; lon?: number } | null | undefined;
      if (!md || typeof md.lat !== "number" || typeof md.lon !== "number") continue;
      const badge = L.marker([md.lat, md.lon], { icon: visitedBadgeIcon(v.visit_type) });
      visitedOverlay.addLayer(badge);
    }
  };

  const onMoveEnd = (): void => {
    window.clearTimeout(moveTimer);
    moveTimer = window.setTimeout(() => {
      const c = map.getCenter();
      saveViewport({ lat: c.lat, lon: c.lng, zoom: map.getZoom() });
      refresh();
    }, MOVE_DEBOUNCE_MS);
  };

  map.on("moveend", onMoveEnd);
  // Immediate first load
  refresh();

  // Use the device id to scope future per-device interactions if needed by callers.
  void ownDeviceId;

  return {
    map,
    osmLayer,
    userLayer,
    visitedOverlay,
    refresh,
    setVisited,
    setUserLocation,
    destroy: () => {
      map.off("moveend", onMoveEnd);
      window.clearTimeout(moveTimer);
      if (inFlight) inFlight.abort();
      map.remove();
    },
    onSelect: (cb) => {
      selectListeners.push(cb);
    },
    onEmpty: (cb) => {
      emptyListeners.push(cb);
    },
  };
}
